<script setup>
import { reactive, computed, ref, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { helpers, requiredIf } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useBranding } from 'shared/composables/useBranding';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  assistantId: {
    type: [Number, String],
    required: true,
  },
  allowedWebHosts: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['submit', 'cancel']);

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_EXTENSIONS = ['.pdf', '.txt', '.csv', '.tsv', '.md'];
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/tab-separated-values',
  'text/markdown',
  'text/x-markdown',
];

const { t } = useI18n();
const { replaceInstallationName } = useBranding();

const formState = {
  uiFlags: useMapGetter('captainDocuments/getUIFlags'),
};

const canAddUrl = computed(() => props.allowedWebHosts.length > 0);

const state = reactive({
  name: '',
  url: '',
  documentType: 'file',
  uploadFile: null,
});
const fileInputRef = ref(null);

const isHttpUrl = value => {
  if (state.documentType !== 'url') return true;
  try {
    const parsed = new URL(String(value || '').trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (error) {
    return false;
  }
};

const validationRules = {
  url: {
    required: requiredIf(() => state.documentType === 'url'),
    url: helpers.withMessage('invalid-url', isHttpUrl),
  },
  uploadFile: {
    required: requiredIf(() => state.documentType === 'file'),
  },
};

const documentTypeOptions = computed(() => {
  const fileOption = {
    value: 'file',
    label: t('CAPTAIN.DOCUMENTS.FORM.TYPE.PDF'),
  };
  if (!canAddUrl.value) return [fileOption];

  return [
    fileOption,
    { value: 'url', label: t('CAPTAIN.DOCUMENTS.FORM.TYPE.URL') },
  ];
});

const v$ = useVuelidate(validationRules, state);
const isLoading = computed(() => formState.uiFlags.value.creatingItem);
const hasFileError = computed(() => v$.value.uploadFile.$error);
const rightsNotice = computed(() =>
  replaceInstallationName(t('CAPTAIN.DOCUMENTS.FORM.URL.RIGHTS_NOTICE'))
);
const hostHint = computed(() =>
  t('CAPTAIN.DOCUMENTS.FORM.URL.HOST_HINT', {
    hosts: props.allowedWebHosts.join(', '),
  })
);
const noHostsHint = computed(() => t('CAPTAIN.DOCUMENTS.FORM.URL.NO_HOSTS'));

const getErrorMessage = (field, errorKey) => {
  return v$.value[field].$error
    ? t(`CAPTAIN.DOCUMENTS.FORM.${errorKey}.ERROR`)
    : '';
};

const formErrors = computed(() => ({
  url: getErrorMessage('url', 'URL'),
  uploadFile: getErrorMessage('uploadFile', 'PDF_FILE'),
}));

watch(canAddUrl, enabled => {
  if (!enabled) state.documentType = 'file';
});

watch(
  () => state.documentType,
  () => {
    v$.value.$reset();
  }
);

const handleCancel = () => emit('cancel');

const fileExtension = fileName => {
  const match = fileName.toLowerCase().match(/(\.[a-z0-9]+)$/);
  return match ? match[1] : '';
};

const isAllowedFile = file => {
  const extension = fileExtension(file.name);
  if (!ALLOWED_EXTENSIONS.includes(extension)) return false;
  if (!file.type) return true;
  return (
    ALLOWED_MIME_TYPES.includes(file.type) || file.type.startsWith('text/')
  );
};

const handleFileChange = event => {
  const file = event.target.files[0];
  if (!file) return;

  if (!isAllowedFile(file)) {
    useAlert(t('CAPTAIN.DOCUMENTS.FORM.PDF_FILE.INVALID_TYPE'));
    event.target.value = '';
    return;
  }

  if (file.size > MAX_FILE_SIZE) {
    useAlert(t('CAPTAIN.DOCUMENTS.FORM.PDF_FILE.TOO_LARGE'));
    event.target.value = '';
    return;
  }

  state.uploadFile = file;
  state.name = file.name.replace(/\.[^.]+$/, '');
};

const openFileDialog = () => {
  nextTick(() => {
    fileInputRef.value?.click();
  });
};

const prepareDocumentDetails = () => {
  const formData = new FormData();
  formData.append('document[assistant_id]', props.assistantId);

  if (state.documentType === 'url') {
    formData.append('document[external_link]', state.url.trim());
    formData.append('document[name]', state.name || state.url.trim());
  } else {
    formData.append('document[file]', state.uploadFile);
    formData.append('document[pdf_file]', state.uploadFile);
    formData.append(
      'document[name]',
      state.name || state.uploadFile.name.replace(/\.[^.]+$/, '')
    );
  }

  return formData;
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;
  emit('submit', prepareDocumentDetails());
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <div v-if="canAddUrl" class="flex flex-col gap-1">
      <label
        for="documentType"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAPTAIN.DOCUMENTS.FORM.TYPE.LABEL') }}
      </label>
      <ComboBox
        id="documentType"
        v-model="state.documentType"
        :options="documentTypeOptions"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>
    <p v-else class="m-0 text-xs text-n-slate-11">
      {{ noHostsHint }}
    </p>

    <template v-if="state.documentType === 'url'">
      <Input
        v-model="state.url"
        :label="t('CAPTAIN.DOCUMENTS.FORM.URL.LABEL')"
        :placeholder="t('CAPTAIN.DOCUMENTS.FORM.URL.PLACEHOLDER')"
        :message="formErrors.url || hostHint"
        :message-type="formErrors.url ? 'error' : 'info'"
      />
      <p class="m-0 text-xs text-n-slate-11">
        {{ rightsNotice }}
      </p>
    </template>

    <div v-else class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.DOCUMENTS.FORM.PDF_FILE.LABEL') }}
      </label>
      <div class="relative">
        <input
          ref="fileInputRef"
          type="file"
          accept=".pdf,.txt,.csv,.tsv,.md,application/pdf,text/plain,text/csv,text/markdown"
          class="hidden"
          @change="handleFileChange"
        />
        <Button
          type="button"
          :color="hasFileError ? 'ruby' : 'slate'"
          :variant="hasFileError ? 'outline' : 'solid'"
          class="!w-full !h-auto !justify-between !py-4"
          @click="openFileDialog"
        >
          <template #default>
            <div class="flex gap-2 items-center">
              <div
                class="flex justify-center items-center w-10 h-10 rounded-lg bg-n-slate-3"
              >
                <i class="text-xl i-ph-file text-n-slate-11" />
              </div>
              <div class="flex flex-col flex-1 gap-1 items-start">
                <p class="m-0 text-sm font-medium text-n-slate-12">
                  {{
                    state.uploadFile
                      ? state.uploadFile.name
                      : t('CAPTAIN.DOCUMENTS.FORM.PDF_FILE.CHOOSE_FILE')
                  }}
                </p>
                <p class="m-0 text-xs text-n-slate-11">
                  {{
                    state.uploadFile
                      ? `${(state.uploadFile.size / 1024 / 1024).toFixed(2)} MB`
                      : t('CAPTAIN.DOCUMENTS.FORM.PDF_FILE.HELP_TEXT')
                  }}
                </p>
              </div>
            </div>
            <i class="i-lucide-upload text-n-slate-11" />
          </template>
        </Button>
      </div>
      <p v-if="formErrors.uploadFile" class="text-xs text-n-ruby-9">
        {{ formErrors.uploadFile }}
      </p>
    </div>

    <Input
      v-model="state.name"
      :label="t('CAPTAIN.DOCUMENTS.FORM.NAME.LABEL')"
      :placeholder="t('CAPTAIN.DOCUMENTS.FORM.NAME.PLACEHOLDER')"
    />

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAPTAIN.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="t('CAPTAIN.FORM.CREATE')"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
